<template>

    <div class="sk-page">

        <!-- 1. En-tête + filtres -->
        <div class="ds-page-head">
            <div>
                <h1 class="ds-page-head__title">Mouvements de stock</h1>
                <p class="ds-page-head__sub">
                    Documents d'entrée, de sortie, de transfert et d'inventaire · du {{ formatDate(dateDebut) }} au {{ formatDate(dateFin) }}
                </p>
            </div>
            <div class="sk-filters">
                <div class="sk-seg" role="group" aria-label="Période">
                    <button v-for="p in presets" :key="p.code" type="button"
                            :class="{ 'sk-seg--on': preset === p.code }"
                            @click="choisirPreset(p.code)">{{ p.label }}</button>
                </div>
                <template v-if="preset === 'perso'">
                    <input type="date" class="sk-input" v-model="dateDebut" :max="dateFin" @change="charger">
                    <input type="date" class="sk-input" v-model="dateFin" :min="dateDebut" @change="charger">
                </template>
                <select class="sk-input" v-model="magasin" aria-label="Magasin">
                    <option value="">Tous les magasins</option>
                    <option v-for="m in magasins" :key="m" :value="m">{{ m }}</option>
                </select>
                <button type="button" class="sk-btn" title="Actualiser" :disabled="chargement" @click="charger(true)">
                    <i class="ri-refresh-line" :class="{ 'sk-spin': chargement }"></i>
                </button>
                <button type="button" class="sk-btn sk-btn--xls" title="Exporter la sélection" :disabled="!docsFiltres.length" @click="exporterExcel">
                    <i class="ri-file-excel-2-line"></i> Excel
                </button>
            </div>
        </div>

        <!-- Données servies par le cache 5 min -->
        <bandeaucachetb v-if="depuisCache" quoi="les mouvements de stock de la période" verbe="sont gardés"
                        manque="Les entrées, sorties, transferts et inventaires saisis depuis n'y figurent pas encore."
                        :heure="heureCache" @actualiser="charger(true)"></bandeaucachetb>

        <!-- 2. Cartes = filtres par type -->
        <div class="sk-kpis">
            <template v-if="charge">
                <div class="sk-kpi sk-kpi--main" :class="{ 'sk-kpi--on': type === '' }" role="button" tabindex="0" @click="type = ''; page = 1">
                    <div class="sk-kpi__lbl">Documents de la période <i class="ri-file-list-3-line"></i></div>
                    <div class="sk-kpi__val">{{ formatNombre(docsPeriode.length) }} <small>documents</small></div>
                    <div class="sk-kpi__sub">soit {{ formatPourcent(docsPeriode.length / nbJours) }} par jour en moyenne</div>
                    <div class="sk-mini">
                        <i v-for="k in codesTypes" :key="k" :style="{ width: (docsPeriode.length ? compteParType[k] / docsPeriode.length * 100 : 0) + '%', background: types[k].clair }"></i>
                    </div>
                </div>
                <div v-for="k in codesTypes" :key="k" class="sk-kpi" :class="{ 'sk-kpi--on': type === k }"
                     :style="{ '--c': types[k].couleur }" role="button" tabindex="0" @click="basculerType(k)">
                    <div class="sk-kpi__lbl">{{ types[k].pluriel }} <i :class="types[k].icone"></i></div>
                    <div class="sk-kpi__val">{{ formatNombre(compteParType[k]) }} <small>{{ docsPeriode.length ? Math.round(compteParType[k] / docsPeriode.length * 100) : 0 }} %</small></div>
                    <div class="sk-kpi__sub">{{ types[k].aide }}</div>
                </div>
            </template>
            <template v-else>
                <div v-for="n in 5" :key="n" class="sk-kpi">
                    <div class="sk-skel" style="width:45%"></div>
                    <div class="sk-skel sk-skel--lg" style="width:60%"></div>
                    <div class="sk-skel" style="width:80%"></div>
                </div>
            </template>
        </div>

        <!-- 3. Activité + utilisateurs -->
        <div class="sk-grid2">
            <div class="sk-card">
                <div class="sk-card__head"><h2><i class="ri-bar-chart-2-line"></i> Activité par jour <span>· nombre de documents par type</span></h2></div>
                <div class="sk-card__body">
                    <apexcharts v-if="charge && docsPeriode.length" type="bar" height="240" :options="optionsActivite" :series="seriesActivite"></apexcharts>
                    <div v-else-if="charge" class="sk-empty"><i class="ri-bar-chart-2-line"></i>Aucun document sur la période.</div>
                    <div v-else class="sk-skel sk-skel--chart"></div>
                </div>
            </div>
            <div class="sk-card">
                <div class="sk-card__head"><h2><i class="ri-team-line"></i> Qui saisit <span>· cliquer pour filtrer</span></h2></div>
                <div class="sk-card__body">
                    <template v-if="charge">
                        <div v-if="!parUtilisateur.length" class="sk-empty"><i class="ri-team-line"></i>Aucun document.</div>
                        <div class="sk-who">
                            <div v-for="u in parUtilisateur" :key="u.nom" class="sk-who__row" :class="{ 'sk-who__row--on': utilisateur === u.nom }"
                                 role="button" tabindex="0" @click="basculerUtilisateur(u.nom)">
                                <span class="sk-av" :style="{ background: couleur(u.nom) }">{{ initiales(u.nom) }}</span>
                                <div class="sk-min0">
                                    <div class="sk-who__name">{{ u.nom }}</div>
                                    <div class="sk-who__bar" :style="{ width: (u.n / parUtilisateur[0].n * 100) + '%' }">
                                        <i v-for="k in codesTypes" :key="k" :style="{ width: (u[k] / u.n * 100) + '%', background: types[k].couleur }"></i>
                                    </div>
                                </div>
                                <span class="sk-who__n">{{ formatNombre(u.n) }}</span>
                            </div>
                        </div>
                    </template>
                    <template v-else><div v-for="n in 4" :key="n" class="sk-skel"></div></template>
                </div>
            </div>
        </div>

        <!-- 4. Journal -->
        <div class="sk-card">
            <div class="sk-toolbar">
                <div class="sk-search">
                    <i class="ri-search-line"></i>
                    <input ref="recherche" type="search" v-model="recherche" placeholder="N° de document, magasin, observation, n° de bon, saisi par..." @keyup.esc="recherche = ''">
                    <kbd v-if="!recherche">/</kbd>
                </div>
            </div>

            <div class="sk-actifs">
                <template v-if="filtresActifs.length">
                    Filtres :
                    <span v-for="f in filtresActifs" :key="f.cle" class="sk-pill">{{ f.label }} <button type="button" title="Retirer" @click="retirerFiltre(f.cle)">✕</button></span>
                </template>
                <template v-else>Aucun filtre</template>
                <span class="sk-sep"></span>
                <span>{{ formatNombre(docsFiltres.length) }} document{{ docsFiltres.length > 1 ? 's' : '' }}</span>
            </div>

            <div class="sk-journal">
                <template v-if="charge">
                    <div v-if="!docsFiltres.length" class="sk-empty">
                        <i class="ri-search-eye-line"></i><b>Aucun document ne correspond.</b><br>Retirez un filtre ou élargissez la période.
                    </div>
                    <template v-for="ligne in lignesJournal" :key="ligne.cle">
                        <div v-if="ligne.jour" class="sk-day">{{ ligne.libelle }}<span>{{ ligne.nb }} document{{ ligne.nb > 1 ? 's' : '' }}</span></div>
                        <div v-else class="sk-doc">
                            <span class="sk-doc__h">{{ ligne.d.heure }}</span>
                            <span class="sk-ic" :style="{ background: types[ligne.d.t].fond, color: types[ligne.d.t].couleur }"><i :class="types[ligne.d.t].icone"></i></span>
                            <div class="sk-min0">
                                <div class="sk-doc__num">
                                    <span v-html="surligner(ligne.d.numdoc)"></span>
                                    <span v-if="ligne.d.numbon" class="sk-bon" v-html="surligner(ligne.d.numbon)"></span>
                                </div>
                                <div class="sk-doc__type" :style="{ color: types[ligne.d.t].couleur }">{{ types[ligne.d.t].label }}</div>
                            </div>
                            <div class="sk-min0">
                                <div class="sk-doc__mag">
                                    <span v-html="surligner(ligne.d.mag)"></span>
                                    <template v-if="ligne.d.vers"><span class="sk-arrow">→</span><span v-html="surligner(ligne.d.vers)"></span></template>
                                </div>
                                <div class="sk-doc__obs" v-html="surligner(ligne.d.obs)"></div>
                            </div>
                            <div class="sk-doc__by">
                                <span class="sk-av sk-av--sm" :style="{ background: couleur(ligne.d.par) }">{{ initiales(ligne.d.par) }}</span>
                                <span v-html="surligner(ligne.d.par)"></span>
                            </div>
                            <button type="button" class="sk-open" @click="btnopen(ligne.d.brut)"><i class="ri-eye-line"></i> Ouvrir</button>
                        </div>
                    </template>
                </template>
                <div v-else class="sk-card__body"><div v-for="n in 8" :key="n" class="sk-skel"></div></div>
            </div>

            <div v-if="charge" class="sk-pager">
                <span v-if="docsFiltres.length">{{ (page - 1) * parPage + 1 }} à {{ Math.min(page * parPage, docsFiltres.length) }} sur {{ docsFiltres.length }}</span>
                <span v-else>0 document</span>
                <span class="sk-pager__pg">
                    <button type="button" :disabled="page <= 1" @click="page--">‹</button>
                    <button v-for="p in pagesVisibles" :key="p" type="button" :class="{ 'sk-pager--on': p === page }" @click="page = p">{{ p }}</button>
                    <button type="button" :disabled="page >= nbPages" @click="page++">›</button>
                </span>
            </div>

            <!-- table d'export (cachée) : toute la sélection -->
            <table ref="exportable_table" class="sk-hidden">
                <thead><tr><th>Date</th><th>Heure</th><th>Type</th><th>N° document</th><th>N° bon</th><th>Magasin</th><th>Vers</th><th>Observation</th><th>Saisi par</th></tr></thead>
                <tbody>
                <tr v-for="d in docsFiltres" :key="'x' + d.cle">
                    <td>{{ formatDate(d.jour) }}</td><td>{{ d.heure }}</td><td>{{ types[d.t].label }}</td><td>{{ d.numdoc }}</td><td>{{ d.numbon }}</td><td>{{ d.mag }}</td><td>{{ d.vers }}</td><td>{{ d.obs }}</td><td>{{ d.par }}</td>
                </tr>
                </tbody>
            </table>
        </div>

        <modalopenform ref="modalopenform"  ></modalopenform>

    </div>

</template>

<script>
    import {currentDateTime, ExportExcel, randomString} from "@/allfunction";
    import * as JwtService from "@/common/jwt.service";
    import modalopenform from "@/components/views/pages/stock/mvtstock/modal_openform";
    import VueApexCharts from "vue3-apexcharts";
    const axios =require('axios');
    import { avecCacheTB } from "@/service/cacheTableauDeBord";
    import bandeaucachetb from "@/components/views/pages/tableaudebord/bandeaucachetb";

    const COULEURS = ['#7c5ae0','#2563eb','#059669','#d97706','#db2777','#0891b2','#65a30d','#9333ea'];

    /* Date locale AAAA-MM-JJ (pas toISOString, qui passe en UTC). */
    const isoLocal = (d) => d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');

    /* Type du document à partir du libellé renvoyé par le serveur (mtypdoc). */
    const codeType = (lib) => {
        const s = String(lib || '').toUpperCase();
        if (s.includes('SORTIE')) return 'S';
        if (s.includes('TRANSFERT')) return 'T';
        if (s.includes('INVENTAIRE')) return 'I';
        return 'E';
    };

    export default {
        /* eslint-disable */
        name: "stock",
        components: {
            bandeaucachetb,
            modalopenform,
            apexcharts: VueApexCharts,
        },
        data() {
            return {
                depuisCache: false,
                heureCache: '',
                initcomponnent:false,
                chargement: false,
                charge: false,

                /* documents bruts renvoyés par l'API */
                datatablehistorique:[],

                presets: [
                    { code: '7', label: '7 jours' },
                    { code: '30', label: '30 jours' },
                    { code: 'mois', label: 'Ce mois' },
                    { code: '90', label: '3 mois' },
                    { code: 'perso', label: 'Dates' },
                ],
                preset: '30',
                dateDebut: '',
                dateFin: '',
                magasin: '',
                type: '',
                utilisateur: '',
                recherche: '',
                page: 1,
                parPage: 25,

                codesTypes: ['E', 'S', 'T', 'I'],
                types: {
                    E: { label: 'Entrée', pluriel: 'Entrées', couleur: '#059669', fond: '#ecfdf5', clair: '#6ee7b7', icone: 'ri-download-2-line', aide: 'réceptions et retours' },
                    S: { label: 'Sortie', pluriel: 'Sorties', couleur: '#dc2626', fond: '#fef2f2', clair: '#fca5a5', icone: 'ri-upload-2-line', aide: 'casses, pertes, consommation' },
                    T: { label: 'Transfert', pluriel: 'Transferts', couleur: '#2563eb', fond: '#eff6ff', clair: '#93c5fd', icone: 'ri-arrow-left-right-line', aide: 'entre magasins' },
                    I: { label: 'Inventaire', pluriel: 'Inventaires', couleur: '#d97706', fond: '#fff7ed', clair: '#fcd34d', icone: 'ri-clipboard-line', aide: 'comptages et ajustements' },
                },
            };
        },
        computed: {
            /* Documents normalisés, du plus récent au plus ancien. */
            documents() {
                return this.datatablehistorique.map((x, i) => {
                    const cree = String(x.creerler || '');
                    const jour = (cree.slice(0, 10) || String(x.datedoc || '').slice(0, 10));
                    const numbon = x.numbon && x.numbon !== '-' ? String(x.numbon) : '';
                    return {
                        cle: (x.pid || '') + i,
                        t: codeType(x.mtypdoc),
                        numdoc: String(x.numdoc || ''),
                        numbon,
                        mag: String(x.magname || ''),
                        vers: x.vmagname ? String(x.vmagname) : '',
                        obs: String(x.observations || ''),
                        par: String(x.saisiepar || '-'),
                        jour,
                        heure: cree.length >= 16 ? cree.slice(11, 16) : '',
                        tri: cree || jour,
                        brut: x,
                    };
                }).sort((a, b) => b.tri.localeCompare(a.tri));
            },

            magasins() {
                return [...new Set(this.documents.flatMap(d => [d.mag, d.vers]).filter(Boolean))].sort();
            },

            /* Période (déjà filtrée par le serveur) + magasin, d'origine ou de destination. */
            docsPeriode() {
                return this.magasin ? this.documents.filter(d => d.mag === this.magasin || d.vers === this.magasin) : this.documents;
            },

            compteParType() {
                const c = { E: 0, S: 0, T: 0, I: 0 };
                for (const d of this.docsPeriode) c[d.t]++;
                return c;
            },

            nbJours() {
                if (!this.dateDebut || !this.dateFin) return 1;
                return Math.max(1, Math.round((new Date(this.dateFin + 'T00:00:00') - new Date(this.dateDebut + 'T00:00:00')) / 86400000) + 1);
            },

            parUtilisateur() {
                const m = {};
                for (const d of this.docsPeriode) {
                    const u = m[d.par] || (m[d.par] = { nom: d.par, n: 0, E: 0, S: 0, T: 0, I: 0 });
                    u.n++; u[d.t]++;
                }
                return Object.values(m).sort((a, b) => b.n - a.n);
            },

            docsFiltres() {
                const q = this.recherche.trim().toLowerCase();
                return this.docsPeriode.filter(d =>
                    (!this.type || d.t === this.type) &&
                    (!this.utilisateur || d.par === this.utilisateur) &&
                    (!q || (d.numdoc + ' ' + d.mag + ' ' + d.vers + ' ' + d.obs + ' ' + d.numbon + ' ' + d.par).toLowerCase().includes(q))
                );
            },

            filtresActifs() {
                const f = [];
                if (this.type) f.push({ cle: 'type', label: this.types[this.type].pluriel });
                if (this.utilisateur) f.push({ cle: 'utilisateur', label: this.utilisateur });
                if (this.magasin) f.push({ cle: 'magasin', label: this.magasin });
                if (this.recherche.trim()) f.push({ cle: 'recherche', label: '« ' + this.recherche.trim() + ' »' });
                return f;
            },

            nbPages() {
                return Math.max(1, Math.ceil(this.docsFiltres.length / this.parPage));
            },

            /* Page courante, avec un en-tête à chaque changement de jour. */
            lignesJournal() {
                const p = Math.min(this.page, this.nbPages);
                const page = this.docsFiltres.slice((p - 1) * this.parPage, p * this.parPage);
                const nbParJour = {};
                for (const d of this.docsFiltres) nbParJour[d.jour] = (nbParJour[d.jour] || 0) + 1;
                const out = [];
                let dernier = null;
                for (const d of page) {
                    if (d.jour !== dernier) {
                        out.push({ cle: 'j' + d.jour, jour: true, libelle: this.libelleJour(d.jour), nb: nbParJour[d.jour] });
                        dernier = d.jour;
                    }
                    out.push({ cle: d.cle, d });
                }
                return out;
            },

            pagesVisibles() {
                let st = Math.max(1, this.page - 2);
                const en = Math.min(this.nbPages, st + 4);
                st = Math.max(1, en - 4);
                const r = [];
                for (let i = st; i <= en; i++) r.push(i);
                return r;
            },

            /* ---------- graphique ---------- */
            joursPeriode() {
                const r = [];
                if (!this.dateDebut || !this.dateFin) return r;
                for (let d = new Date(this.dateDebut + 'T00:00:00'), fin = new Date(this.dateFin + 'T00:00:00'); d <= fin; d.setDate(d.getDate() + 1)) r.push(isoLocal(d));
                return r;
            },
            seriesActivite() {
                const m = {};
                for (const j of this.joursPeriode) m[j] = { E: 0, S: 0, T: 0, I: 0 };
                for (const d of this.docsPeriode) if (m[d.jour]) m[d.jour][d.t]++;
                return this.codesTypes.map(k => ({ name: this.types[k].pluriel, data: this.joursPeriode.map(j => m[j][k]) }));
            },
            optionsActivite() {
                return {
                    chart: { type: 'bar', stacked: true, toolbar: { show: false }, fontFamily: 'inherit', animations: { enabled: false } },
                    colors: this.codesTypes.map(k => this.types[k].couleur),
                    plotOptions: { bar: { columnWidth: '65%', borderRadius: 2 } },
                    dataLabels: { enabled: false },
                    legend: { position: 'top', horizontalAlign: 'right' },
                    grid: { borderColor: '#e2e8f0', strokeDashArray: 3 },
                    xaxis: {
                        categories: this.joursPeriode.map(j => j.slice(8, 10) + '/' + j.slice(5, 7)),
                        tickAmount: this.joursPeriode.length > 16 ? 12 : undefined,
                        labels: { rotate: -45, hideOverlappingLabels: true, style: { colors: '#94a3b8', fontSize: '11px' } },
                    },
                    yaxis: { labels: { formatter: v => Math.round(v), style: { colors: '#94a3b8' } } },
                };
            },
        },
        watch: {
            recherche() { this.page = 1; },
            magasin() { this.page = 1; },
        },
        methods: {

            choisirPreset(code) {
                this.preset = code;
                const now = new Date();
                let d1 = new Date(now);
                if (code === '7') d1.setDate(d1.getDate() - 6);
                if (code === '30') d1.setDate(d1.getDate() - 29);
                if (code === '90') d1.setDate(d1.getDate() - 89);
                if (code === 'mois') d1 = new Date(now.getFullYear(), now.getMonth(), 1);
                if (code === 'perso') {
                    if (!this.dateDebut || !this.dateFin) { this.dateDebut = isoLocal(new Date(now.getFullYear(), now.getMonth(), 1)); this.dateFin = isoLocal(now); }
                    this.charger();
                    return;
                }
                this.dateDebut = isoLocal(d1);
                this.dateFin = isoLocal(now);
                this.charger();
            },

            async charger(force){
                if (this.chargement) return;
                const t = this;
                this.chargement = true;
                let url=process.env.VUE_APP_API_URL+'/jkebfiuebffeznfoieznofibeubqsyudfyegl745874stock771714dfe/stock/sansvalidation/historique/'+this.dateDebut+'/'+this.dateFin+'/'+this.$store.state.appsociete+'/'+randomString();
                try {
                    const r = await avecCacheTB(this.$store.state.appsociete, 'stock', { dateDebut: this.dateDebut, dateFin: this.dateFin }, async () => {
                        const res = await axios.get(url,{
                            headers: {
                                'X-Requested-With': 'XMLHttpRequest',
                                'Accept': 'application/json',
                                'Content-Type': 'application/json',
                                'Authorization': 'Bearer ' + JwtService.getToken(),
                                'csrftoken': this.$store.state.appsociete
                            } ,withCredentials: true // Permet l'envoi de cookies et autres informations d'authentification
                        });
                        return res.data.success && Array.isArray(res.data.m) ? res.data.m : null;
                    }, { force: force === true });
                    t.datatablehistorique = Array.isArray(r.data) ? r.data : [];
                    t.depuisCache = r.cache;
                    t.heureCache = String(new Date(r.t).getHours()).padStart(2, '0') + ':' + String(new Date(r.t).getMinutes()).padStart(2, '0');
                    if (t.magasin && !t.magasins.includes(t.magasin)) t.magasin = '';
                    t.charge = true;
                    t.page = 1;
                } catch (e) {
                    toastr['error']("Une erreur est subvenue. <br/> veuillez actualiser la page.", "<b></b>");
                } finally {
                    t.chargement = false;
                }
            },

            btnopen(value){
                let titlemodal="Document "+value.numdoc
                let Item=value
                this.$refs.modalopenform.showupdate(titlemodal,"update",this.typedoc,Item,this.otherdata);
            },

            loadinitcomponnent(){
                if (this.initcomponnent===false){
                    this.choisirPreset(this.preset);
                    this.initcomponnent=true;
                }
            },

            basculerType(k) {
                this.type = this.type === k ? '' : k;
                this.page = 1;
            },
            basculerUtilisateur(nom) {
                this.utilisateur = this.utilisateur === nom ? '' : nom;
                this.page = 1;
            },
            retirerFiltre(cle) {
                if (cle === 'type') this.type = '';
                if (cle === 'utilisateur') this.utilisateur = '';
                if (cle === 'magasin') this.magasin = '';
                if (cle === 'recherche') this.recherche = '';
                this.page = 1;
            },

            exporterExcel() {
                ExportExcel('xlsx', 'mouvements_stock_' + this.dateDebut + '_' + this.dateFin + '_' + currentDateTime(), undefined, undefined, this.$refs.exportable_table);
            },

            libelleJour(j) {
                const a = new Date(j + 'T00:00:00'), auj = new Date();
                auj.setHours(0, 0, 0, 0);
                const diff = Math.round((auj - a) / 86400000);
                if (diff === 0) return "aujourd'hui";
                if (diff === 1) return 'hier';
                return isNaN(a) ? j : a.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: a.getFullYear() !== auj.getFullYear() ? 'numeric' : undefined });
            },

            echapper(s) {
                return String(s || '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
            },

            /* Surligne la recherche (texte échappé avant insertion). */
            surligner(texte) {
                const s = String(texte || '');
                const q = this.recherche.trim();
                if (!q) return this.echapper(s);
                const i = s.toLowerCase().indexOf(q.toLowerCase());
                if (i < 0) return this.echapper(s);
                return this.echapper(s.slice(0, i)) + '<mark>' + this.echapper(s.slice(i, i + q.length)) + '</mark>' + this.echapper(s.slice(i + q.length));
            },

            couleur(nom) {
                let h = 0;
                for (let i = 0; i < nom.length; i++) h = (h * 31 + nom.charCodeAt(i)) >>> 0;
                return COULEURS[h % COULEURS.length];
            },
            initiales(nom) {
                return nom.split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase();
            },
            formatNombre(v) {
                return (Number(v) || 0).toLocaleString('fr-FR', { maximumFractionDigits: 2 }).replace(/[  ]/g, ' ');
            },
            formatPourcent(v) {
                return (Number(v) || 0).toFixed(1).replace('.', ',');
            },
            formatDate(j) {
                if (!j) return '';
                const s = String(j).slice(0, 10).split('-');
                return s.length === 3 ? s[2] + '/' + s[1] + '/' + s[0] : j;
            },

            raccourciRecherche(e) {
                const tag = (e.target && e.target.tagName) || '';
                if (e.key === '/' && !['INPUT', 'TEXTAREA', 'SELECT'].includes(tag) && this.$el.offsetParent !== null) {
                    e.preventDefault();
                    this.$refs.recherche && this.$refs.recherche.focus();
                }
            },
        },
        mounted() {
            document.addEventListener('keydown', this.raccourciRecherche);
        },
        beforeUnmount() {
            document.removeEventListener('keydown', this.raccourciRecherche);
        },
    }
</script>

<style scoped>

.sk-page { padding-bottom: 1rem; }
.sk-min0 { min-width: 0; }

/* filtres */
.sk-filters { display: flex; flex-wrap: wrap; gap: 0.5rem; align-items: center; }
.sk-seg { display: inline-flex; flex-wrap: wrap; background: #fff; border: 1px solid var(--cf-border); border-radius: var(--cf-radius-sm); padding: 3px; gap: 2px; }
.sk-seg button { border: 0; background: transparent; font-size: 0.78rem; font-weight: 500; padding: 0.28rem 0.65rem; border-radius: var(--cf-radius-xs); color: var(--cf-muted); cursor: pointer; white-space: nowrap; }
.sk-seg button:hover { color: rgb(var(--primary)); }
.sk-seg button.sk-seg--on { background: rgb(var(--primary)); color: #fff; }
.sk-input { background: #fff; border: 1px solid var(--cf-border); border-radius: var(--cf-radius-sm); padding: 0.38rem 0.6rem; font-size: 0.8rem; color: var(--cf-ink); max-width: 100%; }
.sk-btn { height: 2.1rem; padding: 0 0.7rem; display: inline-flex; gap: 0.35rem; align-items: center; background: #fff; border: 1px solid var(--cf-border); border-radius: var(--cf-radius-sm); color: var(--cf-muted); cursor: pointer; font-size: 0.8rem; font-weight: 500; }
.sk-btn:hover:not(:disabled) { color: rgb(var(--primary)); }
.sk-btn:disabled { opacity: 0.5; cursor: default; }
.sk-btn--xls { color: #047857; }
.sk-spin { display: inline-block; animation: sk-rot 0.9s linear infinite; }
@keyframes sk-rot { to { transform: rotate(360deg); } }

/* cartes = filtres par type */
.sk-kpis { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 0.75rem; margin-bottom: 1rem; }
@media (min-width: 1100px) { .sk-kpis { grid-template-columns: 1.2fr 1fr 1fr 1fr 1fr; } }
.sk-kpi { --c: rgb(var(--primary)); background: #fff; border: 1px solid var(--cf-border); border-radius: var(--cf-radius); box-shadow: var(--cf-shadow-sm); padding: 0.9rem 1rem; min-width: 0; cursor: pointer; position: relative; transition: box-shadow 0.15s, transform 0.15s, border-color 0.15s; }
.sk-kpi:hover { box-shadow: var(--cf-shadow-md); transform: translateY(-1px); }
.sk-kpi--on { border-color: var(--c); box-shadow: 0 0 0 2px var(--c); }
.sk-kpi--on::after { content: "filtre actif"; position: absolute; top: 0.55rem; right: 3rem; font-size: 0.62rem; font-weight: 700; color: var(--c); text-transform: uppercase; letter-spacing: 0.03em; }
.sk-kpi__lbl { display: flex; justify-content: space-between; align-items: center; color: var(--cf-muted); font-size: 0.78rem; font-weight: 500; }
.sk-kpi__lbl i { width: 1.9rem; height: 1.9rem; display: inline-flex; align-items: center; justify-content: center; border-radius: 8px; font-size: 1rem; color: var(--c); background: color-mix(in srgb, var(--c) 12%, #fff); }
.sk-kpi__val { font-size: 1.5rem; font-weight: 800; color: var(--cf-ink-strong); margin: 0.35rem 0 0.1rem; font-variant-numeric: tabular-nums; }
.sk-kpi__val small { font-size: 0.75rem; font-weight: 500; color: var(--cf-muted); }
.sk-kpi__sub { font-size: 0.72rem; color: var(--cf-muted); }
.sk-kpi--main { --c: #fff; background: linear-gradient(135deg, rgb(var(--primary)) 0%, #6d4bd0 100%); border: 0; color: #fff; }
.sk-kpi--main .sk-kpi__lbl, .sk-kpi--main .sk-kpi__sub { color: rgb(255 255 255 / 0.85); }
.sk-kpi--main .sk-kpi__lbl i { background: rgb(255 255 255 / 0.18); color: #fff; }
.sk-kpi--main .sk-kpi__val, .sk-kpi--main .sk-kpi__val small { color: #fff; }
.sk-kpi--main.sk-kpi--on { box-shadow: 0 0 0 2px #c4b5fd; }
.sk-mini { display: flex; height: 6px; border-radius: 99px; overflow: hidden; margin-top: 0.5rem; background: rgb(255 255 255 / 0.2); }
.sk-mini i { display: block; height: 100%; }

/* cartes */
.sk-grid2 { display: grid; gap: 1rem; margin-bottom: 1rem; }
@media (min-width: 1024px) { .sk-grid2 { grid-template-columns: minmax(0, 1.8fr) minmax(0, 1fr); } }
.sk-card { background: #fff; border: 1px solid var(--cf-border); border-radius: var(--cf-radius); box-shadow: var(--cf-shadow-sm); min-width: 0; }
.sk-card__head { display: flex; justify-content: space-between; align-items: center; gap: 0.5rem; flex-wrap: wrap; padding: 0.75rem 1rem; border-bottom: 1px solid var(--cf-border); }
.sk-card__head h2 { margin: 0; font-size: 0.9rem; font-weight: 700; color: var(--cf-ink-strong); display: flex; gap: 0.4rem; align-items: center; }
.sk-card__head h2 i { color: rgb(var(--primary)); }
.sk-card__head h2 span { font-size: 0.75rem; font-weight: 400; color: var(--cf-muted); }
.sk-card__body { padding: 0.75rem 1rem; }

/* utilisateurs */
.sk-who { display: grid; gap: 0.5rem; max-height: 250px; overflow: auto; }
.sk-who__row { display: grid; grid-template-columns: 2rem minmax(0, 1fr) auto; gap: 0.6rem; align-items: center; cursor: pointer; border-radius: 8px; padding: 0.2rem 0.3rem; }
.sk-who__row:hover { background: var(--cf-surface); }
.sk-who__row--on { background: rgb(var(--primary) / 0.08); }
.sk-av { width: 2rem; height: 2rem; border-radius: 99px; display: inline-flex; align-items: center; justify-content: center; color: #fff; font-size: 0.7rem; font-weight: 700; flex: 0 0 auto; }
.sk-av--sm { width: 1.6rem; height: 1.6rem; font-size: 0.6rem; }
.sk-who__name { font-size: 0.82rem; font-weight: 600; color: var(--cf-ink-strong); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.sk-who__bar { display: flex; height: 6px; border-radius: 99px; overflow: hidden; background: var(--cf-surface-alt); margin-top: 0.25rem; }
.sk-who__bar i { display: block; height: 100%; }
.sk-who__n { font-weight: 800; font-variant-numeric: tabular-nums; font-size: 0.85rem; }

/* recherche + filtres actifs */
.sk-toolbar { display: flex; flex-wrap: wrap; gap: 0.6rem; align-items: center; padding: 0.85rem 1rem; border-bottom: 1px solid var(--cf-border); }
.sk-search { position: relative; flex: 1 1 300px; min-width: 200px; }
.sk-search > i { position: absolute; left: 0.75rem; top: 50%; transform: translateY(-50%); color: var(--cf-faint); font-size: 1rem; }
.sk-search input { width: 100%; padding: 0.55rem 2.2rem; border: 1px solid var(--cf-border); border-radius: var(--cf-radius-sm); font-size: 0.85rem; background: var(--cf-surface); }
.sk-search input:focus { outline: none; border-color: rgb(var(--primary)); background: #fff; box-shadow: 0 0 0 3px rgb(var(--primary) / 0.12); }
.sk-search kbd { position: absolute; right: 0.6rem; top: 50%; transform: translateY(-50%); font-size: 0.68rem; color: var(--cf-faint); border: 1px solid var(--cf-border); border-radius: 4px; padding: 0 5px; background: #fff; }
.sk-actifs { display: flex; flex-wrap: wrap; gap: 0.4rem; padding: 0.55rem 1rem; border-bottom: 1px solid var(--cf-border); background: var(--cf-surface); align-items: center; font-size: 0.76rem; color: var(--cf-muted); }
.sk-pill { display: inline-flex; gap: 0.3rem; align-items: center; background: #fff; border: 1px solid var(--cf-border); border-radius: 99px; padding: 0.15rem 0.3rem 0.15rem 0.6rem; font-weight: 600; color: var(--cf-ink); }
.sk-pill button { border: 0; background: var(--cf-surface-alt); border-radius: 99px; width: 1.2rem; height: 1.2rem; cursor: pointer; color: var(--cf-muted); display: inline-flex; align-items: center; justify-content: center; font-size: 0.65rem; }
.sk-sep { flex: 1; }

/* journal */
.sk-journal { max-height: 640px; overflow: auto; }
.sk-day { position: sticky; top: 0; z-index: 2; background: var(--cf-surface-alt); padding: 0.45rem 1rem; font-size: 0.74rem; font-weight: 700; color: var(--cf-ink); display: flex; justify-content: space-between; border-bottom: 1px solid var(--cf-border); }
.sk-day::first-letter { text-transform: uppercase; }
.sk-day span { font-weight: 500; color: var(--cf-muted); }
.sk-doc { display: grid; grid-template-columns: 3.2rem 2.4rem minmax(0, 1.3fr) minmax(0, 1.4fr) minmax(0, 1fr) auto; gap: 0.75rem; align-items: center; padding: 0.6rem 1rem; border-bottom: 1px solid var(--cf-surface-alt); }
.sk-doc:hover { background: #faf8ff; }
.sk-doc__h { font-size: 0.75rem; color: var(--cf-muted); font-variant-numeric: tabular-nums; }
.sk-ic { width: 2.2rem; height: 2.2rem; border-radius: 10px; display: inline-flex; align-items: center; justify-content: center; font-size: 1.1rem; }
.sk-doc__num { font-weight: 700; color: var(--cf-ink-strong); font-size: 0.85rem; }
.sk-doc__type { font-size: 0.7rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.02em; }
.sk-doc__mag { font-size: 0.8rem; color: var(--cf-ink); }
.sk-arrow { color: #2563eb; margin: 0 0.25rem; }
.sk-doc__obs { font-size: 0.72rem; color: var(--cf-muted); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.sk-doc__by { font-size: 0.76rem; color: var(--cf-muted); display: flex; gap: 0.4rem; align-items: center; min-width: 0; }
.sk-doc__by > span:last-child { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.sk-bon { display: inline-block; font-size: 0.66rem; font-weight: 700; padding: 1px 6px; border-radius: 5px; background: var(--cf-surface-alt); color: var(--cf-muted); margin-left: 0.3rem; }
.sk-open { border: 1px solid var(--cf-border); background: #fff; border-radius: 8px; height: 2rem; padding: 0 0.7rem; cursor: pointer; font-size: 0.76rem; font-weight: 600; color: rgb(var(--primary)); display: inline-flex; gap: 0.3rem; align-items: center; }
.sk-open:hover { background: rgb(var(--primary)); color: #fff; border-color: rgb(var(--primary)); }
.sk-journal :deep(mark) { background: #fef08a; color: inherit; border-radius: 2px; padding: 0 1px; }

/* pagination */
.sk-pager { display: flex; flex-wrap: wrap; gap: 0.5rem; justify-content: space-between; align-items: center; padding: 0.7rem 1rem; border-top: 1px solid var(--cf-border); font-size: 0.78rem; color: var(--cf-muted); }
.sk-pager__pg { display: flex; gap: 0.25rem; align-items: center; }
.sk-pager__pg button { min-width: 2rem; height: 2rem; border: 1px solid var(--cf-border); background: #fff; border-radius: var(--cf-radius-xs); cursor: pointer; font-size: 0.78rem; color: var(--cf-ink); }
.sk-pager__pg button.sk-pager--on { background: rgb(var(--primary)); border-color: rgb(var(--primary)); color: #fff; }
.sk-pager__pg button:disabled { opacity: 0.4; cursor: default; }

/* états */
.sk-empty { text-align: center; padding: 2.5rem 1rem; color: var(--cf-muted); font-size: 0.85rem; }
.sk-empty i { font-size: 2rem; color: var(--cf-faint); display: block; margin-bottom: 0.4rem; }
.sk-skel { height: 0.75rem; border-radius: 6px; margin: 0.55rem 0; background: linear-gradient(90deg, #eef2f7 25%, #f8fafc 50%, #eef2f7 75%); background-size: 200% 100%; animation: sk-sh 1.3s infinite; }
.sk-skel--lg { height: 1.4rem; }
.sk-skel--chart { height: 220px; }
@keyframes sk-sh { to { background-position: -200% 0; } }
.sk-hidden { display: none; }

@media (max-width: 860px) {
    .sk-doc { grid-template-columns: 2.4rem minmax(0, 1fr) auto; row-gap: 0.2rem; }
    .sk-doc__h, .sk-doc__by { display: none; }
    .sk-doc > div:nth-of-type(2) { grid-column: 2 / 3; }
    .sk-kpi__val { font-size: 1.15rem; }
}

</style>
